import { Computer, Group, Person } from "@mui/icons-material";
import { Box, Chip, capitalize, Divider, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type React from "react";
import type { Task } from "../../types/chat";

interface DoneTasksProps {
  tasks: Task[];
}

export const DoneTasks: React.FC<DoneTasksProps> = ({ tasks }) => {
  // Filter for done tasks only - TypeScript now knows these have DoneTaskInput
  const doneTasks = tasks.filter((task): task is Task & { type: "done" } => task.type === "done");

  const getOutcomeColor = (outcome?: string) => {
    switch (outcome) {
      case "success":
        return "success";
      case "failure":
        return "error";
      case "require_user_input":
        return "warning";
      default:
        return "default";
    }
  };

  const getOwnerIcon = (ownerType: string) => {
    switch (ownerType) {
      case "manager":
        return <Computer fontSize="small" />;
      case "team":
        return <Group fontSize="small" />;
      case "user":
        return <Person fontSize="small" />;
      default:
        return <Computer fontSize="small" />;
    }
  };

  // const formatOutput = (output: any) => {
  //   if (typeof output === "string") {
  //     return output;
  //   }
  //   if (typeof output === "object") {
  //     return JSON.stringify(output, null, 2);
  //   }
  //   return String(output);
  // };
  //
  //
  // const formatInput = (input: any) => {
  //   if (typeof input === "string") {
  //     return input;
  //   }
  //   if (typeof input === "object") {
  //     // For done tasks, show a summary instead of raw JSON
  //     if (input.task_name === "done") {
  //       const summary = [];
  //       if (input.outcome) summary.push(`Outcome: ${input.outcome}`);
  //       if (input.output_format)
  //         summary.push(`Output Format: ${input.output_format}`);
  //       if (input.files_to_display?.length)
  //         summary.push(
  //           `Files to Display: ${input.files_to_display.length} files`,
  //         );
  //       if (input.items_to_review?.length)
  //         summary.push(
  //           `Items to Review: ${input.items_to_review.length} items`,
  //         );
  //       return summary.join("\n");
  //     }
  //
  //     // For other tasks, show relevant fields
  //     const relevantFields = [
  //       "task_name",
  //       "delegated_task_name",
  //       "team_member_id",
  //       "task_description",
  //       "question",
  //       "answer",
  //     ];
  //     const filtered = relevantFields.reduce((acc, key) => {
  //       if (input[key] !== undefined) {
  //         acc[key] = input[key];
  //       }
  //       return acc;
  //     }, {} as any);
  //
  //     if (Object.keys(filtered).length > 0) {
  //       return JSON.stringify(filtered, null, 2);
  //     }
  //     return JSON.stringify(input, null, 2);
  //   }
  //   return String(input);
  // };

  if (doneTasks.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          🎯 No done tasks yet
        </Typography>
        <Typography variant="caption" color="text.secondary" mt={1}>
          Done tasks will appear here when the agent completes major milestones
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} divider={<Divider />} width="100%">
      {doneTasks.reverse().map((task) => (
        <Stack direction="row" alignItems="center" spacing={2} key={task.id} width="100%">
          <Stack direction="column" width="100%" spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Typography variant="subtitle2">
                {task.status === "pending" ? "To Review" : "Ok"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={capitalize(task.input.outcome.replace(/_/g, " "))}
                  size="small"
                  color={getOutcomeColor(task.input.outcome)}
                  variant="filled"
                />
                <Tooltip title={`Owner: ${task.owner_type} (${task.owner_id || "unknown"})`}>
                  <Chip
                    icon={getOwnerIcon(task.owner_type)}
                    label={capitalize(task.owner_type)}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
                {task.real_completed_at && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(task.real_completed_at).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Stack direction="column" spacing={1} divider={<Divider />}>
              {/* Outcome-specific messages */}
              {/*
                    {task.input.outcome === "require_user_input" && (
                      <Alert severity="warning">
                        This task requires user input to proceed.
                      </Alert>
                    )}
                    {task.input.outcome === "failure" && (
                      <Alert severity="error">
                        This task failed to complete successfully.
                      </Alert>
                    )}
                    {task.input.outcome === "success" && (
                      <Alert severity="success">
                        This task completed successfully.
                      </Alert>
                    )}
                    */}

              {/* Input Section */}
              {task.input && (
                <Box>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: "grey.50" }}>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap" }}>
                      {task.input.output.toString()}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Output Section */}
              {/*
                    <Box>
                      <Typography variant="body2" fontWeight="medium" color="text.primary" gutterBottom>
                        📤 Output:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2, backgroundColor: "success.50" }}>
                        <Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap" }}>
                          {formatOutput(task.input.output)}
                        </Typography>
                      </Paper>
                    </Box>
                    */}

              {/* Files to Display */}
              {task.input.files_to_display && task.input.files_to_display.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight="medium" color="text.primary" gutterBottom>
                    Attachments
                  </Typography>
                  <Stack direction="column" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {task.input.files_to_display.map((file: string, idx: number) => (
                      <Chip
                        key={idx}
                        label={file}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ width: "fit-content" }}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}

              {/* Items to Review */}
              {task.input.items_to_review && task.input.items_to_review.length > 0 && (
                <Box>
                  <Typography variant="body2" fontWeight="medium" color="text.primary" gutterBottom>
                    📋 Items to Review:
                  </Typography>
                  <Stack spacing={0.5}>
                    {task.input.items_to_review.map((item: string, idx: number) => (
                      <Typography key={idx} variant="body2" color="text.secondary">
                        • {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};
